import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { ReceiptFormTypes } from "models/formTypes";
import { IconDefinitionConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import {
  SelectInputItemProps,
  SelectInputProps,
  TextInputProps,
  WintFormConnect,
} from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region components
import {
  EditableTable,
  ExpandableSection,
  ExpandableSectionSummary,
  FormFieldLayout,
  SelectInput,
  TextAreaInput,
  TextInput,
  VerticalFormField,
} from "components/common/wintForms";
import { Grid } from "@mui/material";
//#endregion
import { LocalizationHelperInstance, ReceiptHelper } from "utils/helpers";
import { ReceiptTodoCategorySectionProps } from "models/propTypes/receiptPropTypes";

const ReceiptTodoCategorySection: React.FC<ReceiptTodoCategorySectionProps> = ({
  connect,
  receiptCategoryChangeHandler,
  expandableStepProps,
}) => {
  const receiptCategoriesFromStore = useSelector((x: AppState) => x.auth.selectedCompany.receiptCategories ?? []);
  const receiptCategories = React.useMemo<SelectInputItemProps[]>(
    () =>
      receiptCategoriesFromStore.map((item, index) => ({
        key: `receiptCategory-${index}`,
        id: item.id,
        value: item.id,
        label: item.name ?? " ",
        group: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
        data: item,
      })),
    [receiptCategoriesFromStore]
  );

  const receiptCategoryIdConnect = connect("receiptCategoryId");
  const receiptCategoryConnect = connect("receiptCategory");
  const receiptCategoryTypeConnect = connect("receiptCategoryType");
  const personConnect = connect("persons");
  const intentConnect = connect("intent");
  const otherExplanationConnect = connect("otherExplanation");

  const categoryType: WintSuperkollAPIModel.ReceiptCategoryType | undefined = receiptCategoryTypeConnect.value;

  const representation = ReceiptHelper.isRepresentation(categoryType);
  const other = categoryType === WintSuperkollAPIModel.ReceiptCategoryType.Other;

  const onReceiptCategoryChange = (value: number) => {
    const receiptCategory = receiptCategories.find((c) => c.value === value)?.data;

    receiptCategoryChangeHandler?.(receiptCategory?.name ?? "");
    receiptCategoryConnect.setValue(receiptCategory?.name ?? "", false);

    if (value === undefined) {
      receiptCategoryTypeConnect.setValue(undefined, false);
    } else if (receiptCategoryTypeConnect.value !== receiptCategory?.type) {
      receiptCategoryTypeConnect.setValue(receiptCategory?.type);
    }

    receiptCategoryIdConnect.setValue(value, true);
  };

  return (
    <ExpandableSection
      summary={
        <ExpandableSectionSummary
          title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.ReceiptRefersTo)}
        />
      }
      initiallyOpen
      hasErrors={
        receiptCategoryIdConnect.hasErrors ||
        intentConnect.hasErrors ||
        personConnect.hasErrors ||
        otherExplanationConnect.hasErrors
      }
      {...expandableStepProps}
    >
      <VerticalFormField<SelectInputProps>
        Component={SelectInput}
        {...receiptCategoryIdConnect}
        setValue={onReceiptCategoryChange}
        label={TranslationKeys.Receipt.RefersTo}
        icon={IconDefinitionConstants.WVat}
        items={receiptCategories ?? []}
      />
      {representation ? (
        <>
          <VerticalFormField<TextInputProps>
            Component={TextAreaInput}
            {...intentConnect}
            label={TranslationKeys.Common.Purpose}
            icon={IconDefinitionConstants.WCommentDots}
            descriptionText={TranslationKeys.Receipt.ReceiptPurposeHelptext}
            maxLength={300}
          />
          <FormFieldLayout
            direction="vertical"
            descriptionText={
              <Grid container marginTop={-3}>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.ReceiptPersonHelpText)}
              </Grid>
            }
            {...personConnect}
          >
            <EditableTable<ReceiptFormTypes.ReceiptPerson>
              connect={connect as WintFormConnect<any>}
              name={"persons"}
              value={personConnect.value ?? []}
              fields={{
                name: {
                  Component: TextInput,
                  extraProps: {
                    label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Name),
                  },
                  widthColumnCount: 5,
                },
                company: {
                  Component: TextInput,
                  extraProps: {
                    label: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Company),
                  },
                },
              }}
              idField="id"
              defaultRow={{ name: undefined, company: undefined }}
              isCompactTable
            />
          </FormFieldLayout>
        </>
      ) : (
        <></>
      )}
      {other ? (
        <>
          <VerticalFormField<TextInputProps>
            Component={TextInput}
            {...otherExplanationConnect}
            label={TranslationKeys.Receipt.ReceiptOtherExplanationHelptext}
            maxLength={300}
          ></VerticalFormField>
        </>
      ) : (
        <></>
      )}
    </ExpandableSection>
  );
};

export default ReceiptTodoCategorySection;
